/**
 * ComplianceCalendar — month-grid view for compliance items (CompliancePage
 * calendar tab). Items sit on their actual due date (renewals on expiry); clicking a
 * day lists its items below the grid; item names link to detail. The grid
 * itself is the shared MonthCalendar (extracted for the Meetings page).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ClipboardCheck } from 'lucide-react';
import {
  ITEM_STATUS_LABELS, type ComplianceItemOutput, type ItemStatus,
} from '@1financial/shared';
import { MonthCalendar, dayKey } from './MonthCalendar.js';
import { EmptyState } from './EmptyState.js';
import { fmtDate } from '../lib/date.js';

const STATUS_PILL: Record<string, string> = {
  Pending: 'pill-neutral',
  InProcess: 'pill-info',
  ReadyForReview: 'pill-warn',
  Reviewed: 'pill-indigo',
  SentBack: 'pill-danger',
  SentToClient: 'pill-teal',
  Completed: 'pill-success',
};

function itemWhen(item: ComplianceItemOutput): Date | null {
  const iso = item.documentRenewal && item.documentRenewalExpiryDate ? item.documentRenewalExpiryDate : item.dueDate;
  return iso ? new Date(iso) : null;
}

export function ComplianceCalendar({
  items,
  isLoading,
  error,
  clientMap,
}: {
  items: ComplianceItemOutput[] | undefined;
  isLoading: boolean;
  error: Error | null;
  clientMap: Map<string, { name: string; clientCode: string }>;
}) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [selected, setSelected] = useState<string>(dayKey(today));

  const byDay = useMemo(() => {
    const map = new Map<string, ComplianceItemOutput[]>();
    for (const item of items ?? []) {
      const when = itemWhen(item);
      if (!when) continue;
      const key = dayKey(when);
      const arr = map.get(key) ?? [];
      arr.push(item);
      map.set(key, arr);
    }
    return map;
  }, [items]);

  const selectedItems = byDay.get(selected) ?? [];

  function chipClass(item: ComplianceItemOutput): string {
    const when = itemWhen(item);
    if (when && when < today && item.status !== 'Completed') return 'border-rose-700 text-rose-300';
    return 'border-border text-muted hover:border-accent hover:text-white';
  }

  return (
    <div className="space-y-4">
      <MonthCalendar
        items={items}
        isLoading={isLoading}
        error={error}
        dateOf={itemWhen}
        chipKey={(item) => item.id}
        selected={selected}
        onSelect={setSelected}
        renderChip={(item) => (
          <Link
            to={`/compliance/${item.id}`}
            onClick={(e) => e.stopPropagation()}
            title={`${item.serviceType} — ${item.client?.name ?? clientMap.get(item.clientId)?.name ?? ''}`}
            className={clsx('block truncate text-[11px] leading-4 px-1 rounded border', chipClass(item))}
          >
            {item.serviceType}
          </Link>
        )}
      />

      {!isLoading && !error && (
        <div className="bg-bg-panel border border-border rounded-xl p-4">
          <h3 className="text-sm font-medium text-white mb-2">
            {new Date(selected + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long' })}, {fmtDate(selected + 'T00:00:00')}
            <span className="text-xs text-muted font-normal ml-2">{selectedItems.length} item{selectedItems.length === 1 ? '' : 's'}</span>
          </h3>
          {selectedItems.length === 0 ? (
            <p className="text-sm text-muted italic">Nothing due this day.</p>
          ) : (
            <ul className="divide-y divide-border">
              {selectedItems.map((item) => {
                const when = itemWhen(item);
                const overdue = when && when < today && item.status !== 'Completed';
                return (
                  <li key={item.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/compliance/${item.id}`} className="text-sm text-white hover:text-accent block truncate">
                        {item.serviceType}
                      </Link>
                      <p className="text-xs text-muted truncate">{item.client?.name ?? clientMap.get(item.clientId)?.name ?? '—'}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {overdue && <span className="px-2 py-0.5 rounded text-xs border pill-danger">Overdue</span>}
                      <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_PILL[item.status] ?? 'pill-neutral'}`}>
                        {ITEM_STATUS_LABELS[item.status as ItemStatus] ?? item.status}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {(items ?? []).length === 0 && !isLoading && !error && (
        <div className="bg-bg-panel border border-border rounded-xl">
          <EmptyState icon={ClipboardCheck} title="No compliance items match these filters." />
        </div>
      )}
    </div>
  );
}
